import { createApi, type BaseQueryFn } from '@reduxjs/toolkit/query/react';
import type { AxiosRequestConfig } from 'axios';
import { api } from '@/lib/api';

/**
 * The cache layer, over the axios client the app already has.
 *
 * Not `fetchBaseQuery`: the shared client in `@/lib/api-client` carries the
 * access token, the tenant header, `x-lang`, the 401 refresh-and-retry and the
 * FormData content-type rule. A second HTTP path would have to reimplement all
 * of it and would drift from the apps that already use it.
 *
 * What this buys, and why it matters on a slow link: a screen renders from cache
 * immediately and revalidates behind it, identical requests in flight are
 * deduplicated, and a mutation invalidates tags rather than every screen
 * re-fetching everything by hand.
 *
 * It is deliberately NOT a write path for orders. RTK Query's optimistic updates
 * live in memory — a closed tab loses them with no record. Durable writes go
 * through `@/lib/offline`, which persists the mutation with its `operationId`
 * before touching the network.
 */
const axiosBaseQuery =
  (): BaseQueryFn<
    { url: string; method?: AxiosRequestConfig['method']; body?: unknown; params?: unknown },
    unknown,
    { status?: number; message: string }
  > =>
  async ({ url, method = 'get', body, params }) => {
    try {
      const result = await api({ url, method, data: body, params });
      return { data: result.data };
    } catch (error) {
      const response = (error as { response?: { status?: number; data?: { message?: unknown } } })
        .response;
      const message = response?.data?.message;
      return {
        error: {
          status: response?.status,
          message: Array.isArray(message)
            ? message.join(', ')
            : ((message as string) ?? (error as Error).message),
        },
      };
    }
  };

/**
 * Every cacheable domain gets a tag so a write can invalidate exactly what it
 * changed. Added here rather than per-slice because a tag one slice provides
 * and another invalidates has to be spelled the same in both.
 */
export const API_TAGS = [
  'Catalog',
  'Category',
  'Table',
  'Floor',
  'FloorMap',
  'Order',
  'Session',
  'Resource',
  'ResourceSession',
  'Station',
  // A tenant's rate cards, provided and invalidated by
  // `Pages/Pricing/pricingListsApi.ts`. One tag rather than one per resource is
  // the whole point of the model: ten PlayStations attached to "Standard rates"
  // read one cache entry, so editing that card repaints all ten and cannot
  // leave nine of them quoting yesterday's price.
  'PricingList',
  // The venue's guest-facing switches. One entry per branch, provided and
  // invalidated by `Pages/Settings/guestSettingsApi.ts` — declared here because
  // a tag spelled in one file and answered in another has to be spelled once.
  'GuestSettings',
  // §20 — central menu governance. One tag per version id plus a `LIST` index,
  // provided and invalidated by `Pages/MenuGovernance/menuGovernanceApi.ts`.
  // A version is read on its own detail screen far more than it is listed, so
  // it gets its own tag rather than riding `PricingList`'s one-entry-per-family
  // shape — invalidating `MenuVersion:LIST` on every rule edit would repaint
  // every open version's list row for a change none of the others made.
  'MenuVersion',
  // §2 — the integration substrate (ADR-0002), provided and invalidated by
  // `Pages/Integrations/integrationsApi.ts`. One tag covers providers,
  // installations, deliveries and jobs, each keyed by its own `id` — a
  // catalogue, a tenant's installations of it, and everything each
  // installation moves are one family for cache-invalidation purposes even
  // though they are three different tables.
  'Integration',
  // §3 — outbound webhooks (ADR-0004), provided and invalidated by
  // `Pages/Integrations/webhooksApi.ts`. Its own tag rather than riding
  // `Integration`: an endpoint and its deliveries are a different tenant
  // resource from an installation, and invalidating one must not repaint the
  // other screen's list.
  'Webhook',
  // ADR-0028 Part B — delivery aggregators, provided and invalidated by
  // `Pages/DeliveryAggregators/deliveryAggregatorsApi.ts`. Its own tag rather
  // than riding `Integration`: an aggregator order link and an item mapping
  // are read far more often than an installation's own health is, on a
  // screen a floor role opens every few minutes rather than the integrations
  // engineer's occasional one.
  'DeliveryAggregator',
  // ADR-0003 / ADR-0027 — `/public/v1`'s machine credentials, provided and
  // invalidated by `Pages/Integrations/apiClientsApi.ts`. Its own tag, not
  // `Webhook`'s: an `ApiClient` is an INBOUND credential a partner's server
  // holds to call Tarabeeza, the mirror image of a webhook endpoint's
  // outbound one, and the two lists must not repaint each other.
  'ApiClient',
  // ADR-0027 — a tenant's own registered apps (`DeveloperApp` /
  // `DeveloperAppVersion`), provided and invalidated by
  // `Pages/DevPortal/devportalApi.ts`.
  'DeveloperApp',
  // ADR-0027 — the marketplace catalogue this tenant browses and what it has
  // installed (`MarketplaceListing` / `MarketplaceInstallation`), provided and
  // invalidated by `Pages/DevPortal/marketplaceApi.ts`. Its own tag, not
  // `DeveloperApp`: browsing and installing are the READ side of someone
  // ELSE's app, a different tenant relationship from publishing your own.
  'Marketplace',
  // ADR-0023 — franchise/brand governance, provided and invalidated by
  // `Pages/Brands/brandsApi.ts`. Covers the brand row itself, its branch
  // membership, standards and onboarding templates.
  'Brand',
  // ADR-0023 §7 — royalty schemes and statements, its own tag rather than
  // riding `Brand`: generating a statement must repaint the statements list
  // without repainting every open brand card whose own data did not change.
  'Royalty',
] as const;

export const baseApi = createApi({
  reducerPath: 'venueApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: API_TAGS,
  // A venue's catalog does not change while someone is taking an order, and a
  // refetch on every window focus is exactly the request a weak connection
  // cannot afford. Screens that need fresher data set their own interval.
  refetchOnFocus: false,
  refetchOnReconnect: true,
  keepUnusedDataFor: 300,
  endpoints: () => ({}),
});
